/**
 * O aviso de "o cliente encerrou o chat": o que ele diz e para onde ele leva.
 *
 * ## O que faltava
 *
 * O aviso já existia e já abria, e o chamado também: o servidor sempre o cria,
 * com a transcrição em comentário. O que o aviso **não dizia** era QUAL
 * chamado. "Um ticket foi gerado" manda o operador procurar na listagem o que
 * ele já tem na mão -- e foi esse o pedido dele em 02/11/2026: "colocar ali o
 * código do ticket também". Junto vai o **registro do cliente**, pedido nas
 * duas ações de 28/10 ("informar o operador, com o registro do cliente").
 *
 * ## Por que o texto mora aqui
 *
 * Ele aparece em três lugares — a notificação gravada (o sininho), o toast
 * preso no canto e a notificação do navegador — e os três têm de dizer a mesma
 * coisa. Montá-lo em cada um faria os três divergirem no primeiro ajuste de
 * palavra.
 *
 * ## O número que a tela mostra é o `number`
 *
 * Um chamado tem três números e eles não são sinônimos (ver
 * `numerosDoChamado`). O que o operador LÊ na tela é o `number`, e é esse que
 * entra no aviso: mandá-lo procurar por um `id` que a tela não mostra em lugar
 * nenhum seria pior que não mostrar número.
 */

const texto = (valor) => (valor == null ? '' : String(valor).trim());

/**
 * O código do chamado como a tela o escreve, ou `''` quando não veio.
 *
 * Vazio, e não `'#'` nem `'#undefined'`: chamado sem `number` existe (o acervo
 * importado do Movidesk, por exemplo), e nesses casos o aviso tem de ficar sem
 * código em vez de mostrar um código quebrado.
 */
export const codigoDoChamado = (numero) => {
    const limpo = texto(numero);

    return limpo ? `#${limpo}` : '';
};

/**
 * `Fulano (registro 123)` com o que houver, ou `''` quando não se sabe nada.
 *
 * Vazio é resposta: visitante não identificado existe, e as duas frases o
 * tratam de formas diferentes -- uma diz "O cliente", a outra omite o trecho
 * inteiro.
 */
const quemSeSabe = (nome, registro) => {
    const n = texto(nome);
    const r = texto(registro);

    if (n && r) return `${n} (registro ${r})`;
    if (n) return n;
    if (r) return `O cliente (registro ${r})`;

    return '';
};

/** `Fulano (registro 123)`, com o que houver. */
const quemEncerrou = (nome, registro) => quemSeSabe(nome, registro) || 'O cliente';

/** O encerramento veio do relógio, e não do cliente. */
export const POR_INATIVIDADE = 'inatividade';

/**
 * O título do aviso.
 *
 * Dois fatos diferentes pedem dois títulos: "o cliente encerrou" e "ninguém
 * falou nada por um tempo" levam o operador a coisas diferentes -- o primeiro
 * costuma significar assunto resolvido, o segundo costuma pedir retorno.
 *
 * O padrão é o do cliente, e isso importa: uma api que ainda não mande o
 * motivo continua dizendo a coisa certa para o caso que já existia.
 *
 * @param {string} [motivo]
 * @returns {string}
 */
export const tituloDoChatEncerrado = (motivo) => (
    motivo === POR_INATIVIDADE
        ? 'Chat encerrado por inatividade'
        : 'Chat encerrado pelo cliente'
);

/**
 * A frase do aviso.
 *
 * @param {object} dados
 * @param {string} [dados.clientName]    nome do cliente
 * @param {string} [dados.clientCode]    o registro dele (`companies.code`)
 * @param {string|number} [dados.ticketNumber]  o `number` do chamado gerado
 * @param {string} [dados.motivo]        `inatividade` ou o padrão (o cliente)
 * @returns {string}
 */
export const mensagemDeChatEncerrado = ({ clientName, clientCode, ticketNumber, motivo } = {}) => {
    const codigo = codigoDoChamado(ticketNumber);
    const chamado = codigo ? `Chamado ${codigo} gerado.` : 'Um chamado foi gerado.';

    if (motivo === POR_INATIVIDADE) {
        /*
         * Voz passiva aqui, e ativa no outro: quem encerrou foi o relógio, e
         * escrever "Fulano encerrou" seria dizer que o cliente fez algo que
         * ele não fez.
         *
         * Sem nome nem registro a frase perde o "com quem": "A conversa com O
         * cliente foi encerrada" não é português. Visitante não identificado
         * existe, e é justamente quem some sem falar nada.
         */
        const comQuem = quemSeSabe(clientName, clientCode);

        return comQuem
            ? `A conversa com ${comQuem} foi encerrada por inatividade. ${chamado}`
            : `A conversa foi encerrada por inatividade. ${chamado}`;
    }

    return `${quemEncerrou(clientName, clientCode)} encerrou o chat. ${chamado}`;
};

/**
 * O rótulo do botão do aviso.
 *
 * Nomear o chamado no próprio botão é o que transforma "ir ver o que foi
 * gerado" em "abrir o #1234": o operador sabe aonde vai antes de clicar, e
 * reconhece a aba que vai aparecer no acesso rápido.
 */
export const rotuloParaAbrirOChamado = (notificacao) => {
    const dados = notificacao?.data || {};
    const codigo = codigoDoChamado(dados.ticket_number);

    return codigo ? `Abrir chamado ${codigo}` : 'Ir para o chamado';
};

export default mensagemDeChatEncerrado;
